import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { fetchModules } from "@/lib/api";
import AiDrawer from "@/components/AiDrawer";
import {
  LayoutDashboard,
  MapPinned,
  BadgePercent,
  ShoppingBag,
  Users,
  ClipboardList,
  Sparkles,
} from "lucide-react";

const ICONS = {
  wilayah: MapPinned,
  discount: BadgePercent,
  penjualan: ShoppingBag,
  pelanggan: Users,
  program: ClipboardList,
};

export default function Layout() {
  const { data: modules = [] } = useQuery({
    queryKey: ["modules"],
    queryFn: fetchModules,
  });
  const location = useLocation();
  const [aiOpen, setAiOpen] = useState(false);
  const [aiScope, setAiScope] = useState("overview");

  // Derive scope from path
  const currentScope = (() => {
    const m = location.pathname.match(/^\/modul\/([^/]+)/);
    return m ? m[1] : "overview";
  })();

  const openAi = (scope) => {
    setAiScope(scope || currentScope);
    setAiOpen(true);
  };

  // Expose openAi via window for child pages (simple approach)
  if (typeof window !== "undefined") {
    window.__openAi = openAi;
  }

  return (
    <div className="min-h-screen flex bg-[#fafafa]">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className="w-64 border-r border-slate-200 bg-white flex flex-col shrink-0 sticky top-0 h-screen"
      >
        <div className="px-6 py-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0047FF] flex items-center justify-center rounded-sm">
              <span className="font-display font-black text-white text-lg">M</span>
            </div>
            <div>
              <div className="font-display font-black text-[15px] tracking-tight">
                MKI DASHBOARD
              </div>
              <div className="text-[11px] text-slate-500 uppercase tracking-widest mono">
                Marketing 2026
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4">
          <NavLink
            to="/"
            end
            data-testid="nav-overview"
            className={({ isActive }) =>
              `flex items-center gap-3 px-6 py-3 text-sm border-l-2 transition-colors ${
                isActive
                  ? "border-[#0047FF] text-[#0047FF] bg-[#0047FF]/5 font-semibold"
                  : "border-transparent text-slate-700 hover:bg-slate-50"
              }`
            }
          >
            <LayoutDashboard className="w-4 h-4" />
            Ikhtisar
          </NavLink>

          <div className="px-6 pt-6 pb-2 text-[10px] text-slate-400 uppercase tracking-widest mono">
            Modul Data
          </div>

          {modules.map((m) => {
            const Icon = ICONS[m.id] || LayoutDashboard;
            return (
              <NavLink
                key={m.id}
                to={`/modul/${m.id}`}
                data-testid={`nav-${m.id}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3 text-sm border-l-2 transition-colors ${
                    isActive
                      ? "border-[#0047FF] text-[#0047FF] bg-[#0047FF]/5 font-semibold"
                      : "border-transparent text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span className="leading-tight">{m.name}</span>
              </NavLink>
            );
          })}

          <div className="px-6 pt-6 pb-2 text-[10px] text-slate-400 uppercase tracking-widest mono">
            AI
          </div>
          <button
            data-testid="nav-ai"
            onClick={() => openAi(currentScope)}
            className="w-full flex items-center gap-3 px-6 py-3 text-sm border-l-2 border-transparent text-slate-700 hover:bg-[#0047FF]/5 hover:text-[#0047FF] transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            <span className="leading-tight">Buka AI Analisis</span>
          </button>
        </nav>

        <div className="px-6 py-4 border-t border-slate-200 mono text-[11px] text-slate-400">
          v1.1 · Claude Sonnet 4.6
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0" key={location.pathname}>
        <Outlet />
      </main>

      {/* Floating AI button */}
      <button
        data-testid="ai-fab"
        onClick={() => openAi(currentScope)}
        className="fixed bottom-6 right-6 z-40 group flex items-center gap-2 bg-slate-900 text-white pl-3 pr-4 py-3 shadow-lg hover:bg-[#0047FF] transition-colors"
      >
        <Sparkles className="w-5 h-5" />
        <span className="text-sm font-semibold">AI Analisis</span>
      </button>

      <AiDrawer
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        initialScope={aiScope}
      />
    </div>
  );
}
